import * as _ from 'lodash';
import moment from 'moment';
import * as utils from '../util';
import * as actions from '../actions';
import { constants } from '../constants';
import { AddressTypeEnum, TravelDocumentEnum } from '../models/enums';
import { GuestDetails } from '../models/guest-details/guest-details';
import { IGuestInformation, IGuestInformationSavePayload, GuestInformationPayload } from '../models';

export interface State {
  loading: boolean;
  error: null;
  selectedGuestId: number | any;
  loggedInGuestHasLoyalty: any;
  guestDetails: { [guestId: number | string]: GuestDetails };
}

export const initialState: State = {
  loading: false,
  error: null,
  selectedGuestId: null,
  loggedInGuestHasLoyalty: null,
  guestDetails: {}
};

export function reducer(state: State = initialState , action: any): State {
  let newGuestDetailsDictionary: { [guestId: number]: GuestDetails } = {};

  switch (action.type) {
    case actions.GuestDetailsActionTypes.SET_LOAD_BEGIN_DETAILS:
    case actions.GuestDetailsActionTypes.LOAD_BEGIN_DETAILS:
      return {
        ...state,
        loading: true,
        error: null
      };

    case actions.GuestDetailsActionTypes.LOAD_BEGIN_DETAILS_SUCCESS:
      return loadBeginDetailsSuccessReducer(state, action.payload, newGuestDetailsDictionary);

    case actions.GuestDetailsActionTypes.SAVE_GUEST_DETAILS_SUCCESS: {
      newGuestDetailsDictionary = { ...state.guestDetails };
      newGuestDetailsDictionary[state.selectedGuestId] = updateStoreGuestDetails(
        state.guestDetails[state.selectedGuestId],
        (action.payload as GuestInformationPayload<IGuestInformationSavePayload>).guestDetails.guests[0]
      );

      return {
        ...state,
        guestDetails: newGuestDetailsDictionary,
        loggedInGuestHasLoyalty: action.payload.loggedInGuestHasLoyalty
      };
    }

    case actions.GuestDetailsActionTypes.SET_CURRENT_GUEST_ID:
      return {
        ...state,
        selectedGuestId: action.payload
      };

    case actions.GuestDetailsActionTypes.LOAD_GUEST_DOCUMENT_TYPES_SUCCESS:
      return loadGuestDocumentTypesSuccessReducer(state,action.payload);

    case actions.GuestDetailsActionTypes.SAVE_GUEST_SURVEY_SUCCESS:
      return saveGuestSurveySuccessReducer(state, action.payload);

    default:
      return state;
  }
}

export function loadBeginDetailsSuccessReducer(
  state: State,
  response: any,
  newGuestDetailsDictionary: { [guestId: number]: GuestDetails }
): State {
  const paxId = response.loggedInPaxId ? response.loggedInPaxId : null;

  if (response.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
    const actionError = response.errors && response.errors.length ? response.errors[0] : {};
    return {
      ...state,
      loading: false,
      error: actionError,
    };
  } else {
    newGuestDetailsDictionary = { ...state.guestDetails };
    const bookingsGuestsInformation = _.get(response, 'payload.bookings', []);
    let hasLoyalty = false;
    if (!bookingsGuestsInformation.length) {
      return { ...state, loading: false, error: null };
    } else {
      bookingsGuestsInformation.forEach((booking: any) => {
        const guests = booking.guests;
        if (!guests.length) return;
        guests.forEach((guest: any) => {
          newGuestDetailsDictionary[guest.guestId] = mapApiResponse(guest);
          hasLoyalty = paxId === guest.guestId && !!guest.personalInfo.loyaltyId;
        });
      });
      return {
        ...state,
        loading: false,
        loggedInGuestHasLoyalty: hasLoyalty,
        guestDetails: newGuestDetailsDictionary
      };
    }
  }
}

function retrieveDocumentTypesValidation(actionPayload: any) {
  return (
    actionPayload.type === TravelDocumentEnum.LIST &&
    actionPayload.status !== constants.HTTP_STATUS_CODE.SUCCESS
  );
}

function loadGuestDocumentTypesSuccessReducer(state: State, actionPayload: any): State {
  if (retrieveDocumentTypesValidation(actionPayload)) {
    return {
      ...state,
      loading: false,
      error: actionPayload
    };
  }

  let updatedGuestDetails: { [guestId: number | string]: GuestDetails } = {};

  Object.keys(state.guestDetails).forEach(guestId => {
    if (Number(guestId) === actionPayload.guestId) {
      updatedGuestDetails[guestId] = {
        ...state.guestDetails[guestId],
        travelDocsList: actionPayload.documentTypeList
      };
    } else {
      updatedGuestDetails[guestId] = { ...state.guestDetails[guestId] };
    }
  });

  return { ...state, guestDetails: updatedGuestDetails };
}

function saveGuestSurveySuccessReducer(state: State, actionPayload: any): State {
  const newGuestDetails: { [guestId: number | string]: GuestDetails } = {};

  Object.keys(state.guestDetails).forEach(guestId => {
    if (guestId === actionPayload.guestId) {
      newGuestDetails[guestId] = {
        ...state.guestDetails[actionPayload.guestId],
        guestSurvey: actionPayload.guestSurvey
      };
    } else {
      newGuestDetails[guestId] = { ...state.guestDetails[guestId] };
    }
  });

  return { ...state, guestDetails: newGuestDetails };
}

const updateStoreGuestDetails = (gd: GuestDetails, iGuestDetails: IGuestInformation): GuestDetails => {
  const newGuestDetails = { ...gd };
  const {
    guestInformation: {
      personalInfo,
      emergencyContactInfo,
      contactInfo,
      guestAddresses,
      immigrationInfo
    },
  } = iGuestDetails;

  if (personalInfo) {
    newGuestDetails.personalInfo = {
      crownAndAnchorNumber: personalInfo.loyaltyNumber,
      middleName: utils.capitalizeNameAllWords(personalInfo.middleName),
      firstName: utils.capitalizeNameAllWords(personalInfo.firstName),
      lastName: utils.capitalizeNameAllWords(personalInfo.lastName),
      dateOfBirth: personalInfo.birthdate,
      gender: personalInfo.gender,
    };
  }

  if (emergencyContactInfo) {
    newGuestDetails.emergencyContact = {
      fullName: utils.capitalizeNameAllWords(emergencyContactInfo.firstName),
      phoneNumber: emergencyContactInfo.phoneNumber,
      phoneNumberCallingCode: emergencyContactInfo.phoneNumberCallingCode,
      phoneNumberCountryCode: emergencyContactInfo.phoneNumberCountryCode,
    };
  }

  if (contactInfo) {
    newGuestDetails.contactInfo = {
      email: contactInfo.email,
      mobilePhoneNumber: contactInfo.phoneNumber.mobile,
      countryCode: '',
      preferredContactMethod: contactInfo.prefferedContactMethod,
      numberCallingCode: contactInfo.phoneNumber.numberCallingCode,
      numberCountryCode: contactInfo.phoneNumber.numberCountryCode,
    };
  }

  const residenceAddress = guestAddresses?.find(({ addressType }) => addressType === AddressTypeEnum.residenceAddress);
  newGuestDetails.homeAddress = {
    addressLine1: residenceAddress?.line1 ?? '',
    addressLine2: residenceAddress?.line2,
    country: residenceAddress?.countryCode ?? '',
    stateOrProvince: residenceAddress?.stateCode ?? '',
    city: residenceAddress?.city ?? '',
    postalCode: residenceAddress?.postalCode ?? '',
  };

  if (immigrationInfo) {
    const postCruiseAddress = guestAddresses?.find(({ addressType }) => addressType === AddressTypeEnum.postCruiseAddress) || null;
    const { documents: [document] } = immigrationInfo;
    newGuestDetails.immigrationInfo = {
      documentType: document.type,
      countryOfResidence: document.countryCode,
      temporaryAddress: postCruiseAddress
        ? {
            address: postCruiseAddress.line1,
            city: postCruiseAddress.city,
            stateCode: postCruiseAddress.stateCode,
            zipCode: postCruiseAddress.postalCode
          }
        : {
          address: '',
          city: '',
          stateCode: '',
          zipCode: ''
        },
      driverLicenseState: document.driverLicenseState,
      countryOfCitizenship: immigrationInfo.countryOfCitizenship,
      countryOfBirth: immigrationInfo.countryOfBirth,
      documentExpirationDate: document.expiryDate,
      documentIssuingState: document.issuingState,
      photoDocumentType: document.photoType,
      documentNumber: document.number,
    };
  }

  return newGuestDetails;
};

export function mapApiResponse(guest: any): GuestDetails {
  const immigrationInfo = guest.immigrationInfo;
  const mappedImmigrationInfo = mapTravelDocument(immigrationInfo);
  const residentialAddress = guest.guestAddresses.find((a: any) => a.addressType === constants.GUEST_ADDRESSES_TYPES.RESIDENTIAL_ADDRESS);
  const postCruiseAddress = guest.guestAddresses.find((a: any) => a.addressType === constants.GUEST_ADDRESSES_TYPES.POST_ADDRESS);
  
  // tslint:disable-next-line: no-shadowed-variable
  return {
    guestId: guest.guestId,

    personalInfo: {
      firstName: utils.capitalizeNameAllWords(guest.personalInfo.firstName),
      lastName: utils.capitalizeNameAllWords(guest.personalInfo.lastName),
      middleName: utils.capitalizeNameAllWords(guest.personalInfo.middleName),
      gender: guest.personalInfo.gender,
      crownAndAnchorNumber: guest.personalInfo.loyaltyId ?? '',
      dateOfBirth: moment(guest.personalInfo.birthdate, constants.DATE_FORMAT.MICROSERVICE).format(constants.DATE_FORMAT.SERVER)
    },
    travelPhoto: {
      photoId: _.get(guest, 'travelPhoto.photoId', null),
      photoUri: _.get(guest, 'travelPhoto.photoUri', '')
    },
    homeAddress: {
      addressLine1: residentialAddress?.addressLine1 ?? '',
      addressLine2: residentialAddress?.addressLine2 ?? '',
      postalCode: residentialAddress?.postalCode ?? '',
      city: residentialAddress?.city ?? '',
      stateOrProvince: residentialAddress?.stateCode ?? '',
      country: residentialAddress?.countryCode ?? ''
    },
    immigrationInfo: {
      countryOfBirth: immigrationInfo.countryOfBirth,
      countryOfCitizenship: immigrationInfo.countryOfCitizenship !== ' ' ? immigrationInfo.countryOfCitizenship : '',
      countryOfResidence: immigrationInfo.countryOfResidence,
      documentExpirationDate: mappedImmigrationInfo.documentExpirationDate,
      documentNumber: mappedImmigrationInfo.documentNumber,
      documentType: immigrationInfo.documentType,
      driverLicenseState: mappedImmigrationInfo.driverLicenseState,
      documentIssuingState: mappedImmigrationInfo.documentIssuingState,
      photoDocumentType: mappedImmigrationInfo.photoDocumentType,
      temporaryAddress: {
        address: _.get(postCruiseAddress, 'address', ''),
        city: _.get(postCruiseAddress, 'city', ''),
        stateCode: _.get(postCruiseAddress, 'stateCode', ''),
        zipCode: _.get(postCruiseAddress, 'postalCode', '')
      }
    },
    contactInfo: {
      email: guest.contactInfo.email,
      mobilePhoneNumber: guest.contactInfo.phoneNumber?.number ?? '',
      countryCode: guest.contactInfo.phoneNumber?.countryCode ?? '',
      numberCallingCode: guest.contactInfo.phoneNumber?.numberCallingCode ?? '',
      numberCountryCode: guest.contactInfo.phoneNumber?.numberCountryCode ?? '',
      preferredContactMethod: guest.contactInfo.preferredContactMethod ?? ''
    },
    emergencyContact: mapEmergencyContact(guest.emergencyContactInfo),
    travelDocsList: guest.validTravelDocuments ?? [],
    guestSurvey: {
      cruiseCountWithUs: guest.cruiseSurvey.cruiseCountWithUs,
      cruiseCount: guest.cruiseSurvey.cruiseCount,
      firstCruiseVacation: guest.cruiseSurvey.firstCruiseVacation
    }
  };
}

function mapEmergencyContact(info: any): GuestDetails['emergencyContact'] {
  return {
    fullName: utils.capitalizeNameAllWords(info.name),
    phoneNumber: info.phoneNumber,
    phoneNumberCallingCode: info.phoneNumberCallingCode ?? '',
    phoneNumberCountryCode: info.phoneNumberCountryCode ?? ''
  };
}

export function mapTravelDocument(immigrationInfo: any) {
  const mappedFields = {
    documentExpirationDate: '',
    documentNumber: '',
    driverLicenseState: '',
    documentIssuingState: '',
    photoDocumentType: ''
  };
  if (immigrationInfo.documents?.length) {
    immigrationInfo.documents.forEach((d: any) => {
      mappedFields.documentExpirationDate = d.expiryDate
        ? moment(d.expiryDate, constants.DATE_FORMAT.MICROSERVICE).format(constants.DATE_FORMAT.SERVER) : '';
      mappedFields.documentNumber = d.number ?? '';
      mappedFields.documentIssuingState =
        mappedFields.documentIssuingState === '' && d.issuingState && constants.TRAVEL_DOCUMENT.ISSUING_STATE.includes(d.type)
          ? d.issuingState
          : mappedFields.documentIssuingState;
      mappedFields.driverLicenseState =
        mappedFields.driverLicenseState === '' && d.issuingState && constants.TRAVEL_DOCUMENT.DRIVER_LICENSE_STATE.includes(d.type)
          ? d.issuingState
          : mappedFields.driverLicenseState;
      mappedFields.photoDocumentType = '';
    });
  }
  return mappedFields;
}

export const loading = (state: State) => state.loading;
export const selectedGuestId = (state: State) => state.selectedGuestId;
export const guestDetails = (state: State) => state.guestDetails;
export const error = (state: State) => state.error;